import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import AllSubjectsMarksModal from './parts/AllSubjectsMarksModal';
import BatchFilterControl from '../../components/BatchSelector';

interface Batch {
  batch_id: string;
  batch_name: string;
  batch_time: string;
  course_id: string;
  course_name?: string;
  course_title?: string;
  is_active: boolean;
}

interface Subject {
  subject_id: string;
  subject_name: string;
  max_marks: number;
  practical_max_marks: number;
}

// Student now carries its own marks status directly — no separate
// marksStatus state/fetch to keep in sync, so no race condition possible.
interface Student {
  student_id: string;
  name: string;
  student_custom_id?: string;
  mobileNumber?: string;
  profilePicture?: any;
  marks_recorded: number;
  marks_total: number;
}

interface BatchInfo {
  batch_id: string;
  batch_name: string;
  batch_time: string;
  course_id: string;
  course_name: string | null;
  course_title: string | null;
  is_active: boolean;
}

const RecordMarks: React.FC = () => {
  // Kept locally (single lightweight fetch, separate from BatchFilterControl's own
  // paginated fetch) so we can look up course_id for the selected batch and show the
  // batch name in the header — BatchFilterControl only reports the id back.
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [batchInfo, setBatchInfo] = useState<BatchInfo | null>(null);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Student currently open in the "All Subjects" modal. Non-null => modal visible.
  const [allSubjectsStudent, setAllSubjectsStudent] = useState<Student | null>(null);

  const { profile, fetchProfile } = useOperatorProfileStore();
  const INSTITUTE_ID = profile?.institute_id;
  const BRANCH_ID = profile?.branch_id;

  // Ensure operator profile (and therefore institute_id/branch_id) is loaded.
  useEffect(() => {
    if (!profile) {
      fetchProfile();
    }
  }, []);

  // Fetch batches on mount
  useEffect(() => {
    fetchBatches();
  }, []);

  // Fetch subjects and students-with-marks-status whenever the batch changes.
  useEffect(() => {
    if (selectedBatchId) {
      setSubjects([]);
      setStudents([]);
      fetchSubjectsForBatch(selectedBatchId);
      fetchStudentsWithMarksStatus(selectedBatchId);
    } else {
      setSubjects([]);
      setStudents([]);
    }
  }, [selectedBatchId]);

  const fetchBatches = async () => {
    try {
      setLoadingBatches(true);
      const response = await apiClient.get('/v1/batches');
      if (response.data.success) {
        const activeBatches = response.data.data.filter((b: Batch) => b.is_active);
        setBatches(activeBatches);
        if (activeBatches.length > 0 && !selectedBatchId) {
          setSelectedBatchId(activeBatches[0].batch_id);
        }
      } else {
        setError('Failed to load batches');
      }
    } catch (err: any) {
      setError('Error loading batches');
    } finally {
      setLoadingBatches(false);
    }
  };

  const fetchSubjectsForBatch = async (batchId: string) => {
    try {
      const batch = batches.find((b) => b.batch_id === batchId);
      if (!batch) return;
      const courseId = batch.course_id;

      const response = await apiClient.get(`/v1/subjects/by-course/${courseId}`);
      if (response.data.success) {
        const subjectList = response.data.data.subjects.map((s: any) => ({
          subject_id: s.subject_id,
          subject_name: s.subject_name,
          max_marks: s.max_marks || 0,
          practical_max_marks: s.practical_max_marks || 0,
        }));
        setSubjects(subjectList);
        setBatchInfo({
          batch_id: batchId,
          batch_name: batch.batch_name,
          batch_time: batch.batch_time,
          course_id: courseId,
          course_name: batch.course_name || null,
          course_title: batch.course_title || null,
          is_active: batch.is_active,
        });
      } else {
        setError('Failed to load subjects');
      }
    } catch (err: any) {
      setError('Error loading subjects');
    }
  };

  const fetchStudentsWithMarksStatus = async (batchId: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get(`/v1/students/get-students-by-batch-with-marks/${batchId}`);
      if (!res.data.success) {
        throw new Error(res.data.error || 'Failed to fetch students');
      }
      const studentList: Student[] = res.data.data.map((s: any) => ({
        student_id: s.student_id,
        name: s.name,
        student_custom_id: s.student_custom_id,
        mobileNumber: s.mobileNumber,
        profilePicture: s.profilePicture,
        marks_recorded: s.marks_recorded ?? 0,
        marks_total: s.marks_total ?? 0,
      }));
      setStudents(studentList);
    } catch (err: any) {
      setError(err.message || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const refreshStatus = () => {
    if (selectedBatchId) {
      fetchStudentsWithMarksStatus(selectedBatchId);
    }
  };

  const renderStudentName = (rowData: Student) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <Avatar
          image={rowData.profilePicture?.cdn_url}
          icon={!rowData.profilePicture ? 'pi pi-user' : undefined}
          size="normal"
          shape="circle"
          style={{ width: '36px', height: '36px', backgroundColor: '#E8EEF7', color: '#0e225d' }}
        />
        <div>
          <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#0e225d' }}>{rowData.name}</div>
          <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{rowData.student_custom_id || ''}</div>
        </div>
      </div>
    );
  };

  const renderMarksStatus = (rowData: Student) => {
    const total = rowData.marks_total;
    if (!total || total === 0) {
      return <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>No subjects</span>;
    }

    const recorded = rowData.marks_recorded;
    const remaining = Math.max(total - recorded, 0);
    const pct = Math.round((recorded / total) * 100);
    const isComplete = remaining === 0;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '150px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', fontWeight: 700 }}>
          <span style={{ color: isComplete ? '#15803d' : '#0e225d' }}>{recorded}/{total} recorded</span>
          <span style={{ color: isComplete ? '#15803d' : '#dc2626' }}>
            {isComplete ? 'Complete' : `${remaining} left`}
          </span>
        </div>
        <div style={{ width: '100%', height: '6px', background: '#f0f1f3', borderRadius: '4px', overflow: 'hidden' }}>
          <div
            style={{
              width: `${pct}%`,
              height: '100%',
              background: isComplete
                ? 'linear-gradient(90deg,#22c55e,#16a34a)'
                : 'linear-gradient(90deg,#fbb21f,#e09a00)',
              borderRadius: '4px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>
    );
  };

  const renderActions = (rowData: Student) => (
    <button
      onClick={() => setAllSubjectsStudent(rowData)}
      title="Enter marks for all subjects at once"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        background: 'white',
        border: '1.5px solid #0e225d',
        borderRadius: '8px',
        color: '#0e225d',
        fontSize: '0.75rem',
        fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      <i className="pi pi-list"></i> All Subjects
    </button>
  );

  const colHeaderStyle: React.CSSProperties = {
    background: '#fafafa',
    color: '#9ca3af',
    fontSize: '0.72rem',
    fontWeight: '700',
    textTransform: 'uppercase',
    borderBottom: '2px solid #E5E7EB',
    padding: '12px 16px',
    letterSpacing: '0.6px',
  };

  const colBodyStyle: React.CSSProperties = {
    padding: '10px 16px',
    borderBottom: '1px solid #f3f4f6',
  };

  if (loadingBatches) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" />
      </div>
    );
  }

  return (
    <div style={{ padding: '0', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Batch Selection */}
      <div style={{ background: 'white', borderRadius: '14px', padding: '14px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <div style={{ width: '4px', height: '18px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
          <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0e225d', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="pi pi-th-large" style={{ fontSize: '0.8rem' }}></i>
            Select Batch
          </span>
        </div>

        <BatchFilterControl
          selectedBatchId={selectedBatchId}
          onBatchChange={setSelectedBatchId}
        />
      </div>

      {selectedBatchId && (
        <>
          {/* Error */}
          {error && <Message severity="error" text={error} style={{ width: '100%' }} />}

          {/* Students Table */}
          <div style={{ background: 'white', borderRadius: '14px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB', overflow: 'hidden' }}>
            <div
              style={{
                padding: '14px 18px',
                borderBottom: '1px solid #E5E7EB',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '4px', height: '22px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
                <h2 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#0e225d' }}>
                  Record Marks
                  {batchInfo && (
                    <span style={{ fontSize: '0.7rem', fontWeight: '400', color: '#9ca3af', marginLeft: '8px' }}>
                      {batchInfo.batch_name}
                    </span>
                  )}
                </h2>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="3" />
              </div>
            ) : (
              <DataTable value={students} dataKey="student_id" emptyMessage="No students enrolled in this batch." style={{ fontSize: '0.85rem' }}>
                <Column field="name" header="Student" body={renderStudentName} headerStyle={colHeaderStyle} bodyStyle={colBodyStyle} />
                <Column
                  field="marksStatus"
                  header="Subjects Recorded"
                  body={renderMarksStatus}
                  headerStyle={colHeaderStyle}
                  bodyStyle={colBodyStyle}
                />
                <Column
                  field="actions"
                  header="Actions"
                  body={renderActions}
                  headerStyle={colHeaderStyle}
                  bodyStyle={{ ...colBodyStyle, textAlign: 'center' }}
                />
              </DataTable>
            )}
          </div>
        </>
      )}

      {/* All Subjects Modal */}
      <AllSubjectsMarksModal
        visible={!!allSubjectsStudent}
        onHide={() => {
          setAllSubjectsStudent(null);
          refreshStatus();
        }}
        student={allSubjectsStudent}
        batchId={selectedBatchId}
        subjects={subjects}
        instituteId={INSTITUTE_ID}
        branchId={BRANCH_ID}
        onSaved={refreshStatus}
      />
    </div>
  );
};

export default RecordMarks;